import { useState, useEffect } from "react";
import axios from "axios";
import { Settings, Save, Code, BarChart3, Search, FileCode, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SeoSettingsManager() {
  const [settings, setSettings] = useState({
    site_title: "",
    site_description: "",
    google_analytics: "",
    google_search_console: "",
    custom_head_code: ""
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/seo-settings`);
      setSettings(res.data);
    } catch (err) {
      console.error("Blad pobierania ustawien SEO:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/seo-settings`, settings);
      toast.success("Ustawienia SEO zapisane!");
    } catch (err) {
      toast.error("Blad zapisu ustawien");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Ustawienia SEO strony glownej
          </CardTitle>
          <CardDescription>
            Konfiguracja meta tagow i kodow sledzacych dla epidemos.pl
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Meta tagi */}
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="site_title" className="flex items-center gap-2">
                <Search className="w-4 h-4 text-slate-400" />
                Tytul strony (meta title)
              </Label>
              <Input
                id="site_title"
                value={settings.site_title}
                onChange={(e) => setSettings({ ...settings, site_title: e.target.value })}
                placeholder="Epidemos - Marketplace publikacji..."
                maxLength={70}
              />
              <p className="text-xs text-slate-400">{settings.site_title?.length || 0}/70 znakow (zalecane 50-60)</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="site_description" className="flex items-center gap-2">
                <FileCode className="w-4 h-4 text-slate-400" />
                Opis strony (meta description)
              </Label>
              <Textarea
                id="site_description"
                value={settings.site_description}
                onChange={(e) => setSettings({ ...settings, site_description: e.target.value })}
                placeholder="Epidemos to nowoczesna platforma..."
                rows={3}
                maxLength={160}
              />
              <p className="text-xs text-slate-400">{settings.site_description?.length || 0}/160 znakow (zalecane 150-155)</p>
            </div>
          </div>

          <div className="border-t pt-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-blue-600" />
              Kody sledzace
            </h3>
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="google_analytics" className="text-sm">
                  Google Analytics (GA4 Measurement ID)
                </Label>
                <Input
                  id="google_analytics"
                  value={settings.google_analytics}
                  onChange={(e) => setSettings({ ...settings, google_analytics: e.target.value })}
                  placeholder="G-XXXXXXXXXX"
                />
                <p className="text-xs text-slate-400">Wklej ID pomiaru z Google Analytics 4</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="google_search_console" className="text-sm">
                  Google Search Console (kod weryfikacyjny)
                </Label>
                <Input
                  id="google_search_console"
                  value={settings.google_search_console}
                  onChange={(e) => setSettings({ ...settings, google_search_console: e.target.value })}
                  placeholder="google-site-verification=XXXX..."
                />
                <p className="text-xs text-slate-400">Wklej caly meta tag lub tylko wartosc content</p>
              </div>
            </div>
          </div>

          <div className="border-t pt-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Code className="w-4 h-4 text-purple-600" />
              Dodatkowy kod w sekcji head
            </h3>
            <div className="space-y-2">
              <Label htmlFor="custom_head_code" className="text-sm">
                Niestandardowy kod HTML/JS
              </Label>
              <Textarea
                id="custom_head_code"
                value={settings.custom_head_code}
                onChange={(e) => setSettings({ ...settings, custom_head_code: e.target.value })}
                placeholder="<!-- Dodatkowe skrypty, meta tagi, itp. -->"
                rows={5}
                className="font-mono text-sm"
              />
              <p className="text-xs text-slate-400">
                Ten kod zostanie dodany do sekcji &lt;head&gt; na wszystkich stronach.
                Mozesz tu wkleic np. Facebook Pixel, Hotjar, itp.
              </p>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Zapisywanie...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Zapisz ustawienia</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Podglad */}
      <Card className="bg-slate-50">
        <CardHeader>
          <CardTitle className="text-sm">Podglad w wyszukiwarce Google</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-white p-4 rounded-lg border">
            <div className="text-blue-700 text-lg hover:underline cursor-pointer">
              {settings.site_title || "Epidemos - Marketplace publikacji"}
            </div>
            <div className="text-green-700 text-sm">https://epidemos.pl</div>
            <div className="text-slate-600 text-sm mt-1">
              {settings.site_description || "Opis strony pojawi sie tutaj..."}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
